<script>
  // Шторка действий поверх BottomSheet.
  //
  // actions: [{ id, label, icon?, danger?, disabled?, confirm?, input?, children? }]
  //  - confirm: строка вопроса — тап раскрывает inline-подтверждение внутри шторки
  //    («дорогие»/деструктивные действия), select уходит только после «Да»;
  //  - input: { value?, placeholder?, submitLabel? } — тап раскрывает поле ввода
  //    (переименование), select получает { id, value };
  //  - children: вложенный список действий (1.23.3: «Ещё…», «Удалить…») — тап
  //    показывает его на месте с кнопкой «Назад»; select уходит с id дочернего пункта;
  //  - icon: ключ из ICONS (lib/icons.js, инлайновые SVG-глифы, stroke 2px).
  // События: select({ id, value? }), close.
  import { createEventDispatcher } from 'svelte';
  import BottomSheet from './BottomSheet.svelte';
  import { TEXT } from '../ui-text.js';
  import { ICONS } from '../icons.js';

  export let open = false;
  export let title = '';
  export let actions = [];

  const dispatch = createEventDispatcher();

  // Открытое подменю (пункт с children) — null на первом уровне.
  let submenu = null;
  $: visibleActions = submenu ? submenu.children : actions;
  $: if (!open && submenu !== null) {
    submenu = null;
  }


  let expandedId = null;
  let inputValue = '';

  $: if (!open && expandedId !== null) {
    expandedId = null;
  }

  function handleTap(action) {
    if (action.disabled) {
      return;
    }
    if (Array.isArray(action.children)) {
      expandedId = null;
      submenu = action;
      return;
    }
    if (action.confirm || action.input) {
      if (expandedId === action.id) {
        expandedId = null;
        return;
      }
      expandedId = action.id;
      inputValue = action.input?.value ?? '';
      return;
    }
    dispatch('select', { id: action.id });
  }

  function submitExpanded(action) {
    const detail = { id: action.id };
    if (action.input) {
      detail.value = inputValue;
    }
    expandedId = null;
    dispatch('select', detail);
  }

  function handleInputKeydown(event, action) {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitExpanded(action);
    }
  }

  function close() {
    expandedId = null;
    submenu = null;
    dispatch('close');
  }

  function goBack() {
    expandedId = null;
    submenu = null;
  }
</script>

<BottomSheet {open} title={submenu ? submenu.label : title} on:close={close}>
  {#if submenu}
    <button type="button" class="sheet-item sheet-back" on:click={goBack}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      <span>{TEXT.chat.sheetBack}</span>
    </button>
  {/if}
  {#each visibleActions as action (action.id)}
    <button
      type="button"
      class="sheet-item"
      class:danger={action.danger}
      disabled={action.disabled}
      aria-expanded={action.confirm || action.input ? expandedId === action.id : undefined}
      aria-current={action.checked ? 'true' : undefined}
      on:click={() => handleTap(action)}
    >
      {#if action.icon && ICONS[action.icon]}
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d={ICONS[action.icon]} />
        </svg>
      {/if}
      <span>{action.label}</span>
      {#if action.checked}
        <span class="sheet-check" aria-hidden="true">✓</span>
      {/if}
      {#if Array.isArray(action.children)}
        <span class="sheet-chevron" aria-hidden="true">›</span>
      {/if}
    </button>
    {#if expandedId === action.id && action.confirm}
      <div class="expand-box">
        <p>{action.confirm}</p>
        <div class="expand-actions">
          <button type="button" class="expand-yes" class:danger={action.danger} on:click={() => submitExpanded(action)}>
            {action.label}
          </button>
          <button type="button" class="expand-no" on:click={() => (expandedId = null)}>Отмена</button>
        </div>
      </div>
    {:else if expandedId === action.id && action.input}
      <div class="expand-box">
        <!-- svelte-ignore a11y-autofocus : поле раскрыто осознанным тапом -->
        <input
          type="text"
          bind:value={inputValue}
          placeholder={action.input.placeholder || ''}
          aria-label={action.label}
          autofocus
          on:keydown={(event) => handleInputKeydown(event, action)}
        />
        <div class="expand-actions">
          <button type="button" class="expand-yes" on:click={() => submitExpanded(action)}>
            {action.input.submitLabel || 'Сохранить'}
          </button>
          <button type="button" class="expand-no" on:click={() => (expandedId = null)}>Отмена</button>
        </div>
      </div>
    {/if}
  {/each}
  <button type="button" class="sheet-cancel" on:click={close}>Отмена</button>
</BottomSheet>

<style>
  .sheet-check {
    margin-left: auto;
    color: var(--accent-color);
    font-weight: 600;
  }

  .sheet-chevron {
    margin-left: auto;
    color: var(--text-muted);
    font-size: 1.2rem;
    line-height: 1;
  }

  .sheet-back {
    color: var(--text-secondary);
    border-bottom: 1px solid var(--border-light);
    border-radius: 0;
    margin-bottom: 0.25rem;
  }

  .sheet-item {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    width: 100%;
    min-height: 52px;
    padding: 0 0.5rem;
    border: none;
    background: none;
    color: var(--text-primary);
    font-size: 1rem;
    text-align: left;
    border-radius: 10px;
  }

  .sheet-item svg {
    color: var(--text-secondary);
    flex: 0 0 auto;
  }

  .sheet-item.danger,
  .sheet-item.danger svg {
    color: var(--error-text);
  }

  .sheet-item:disabled {
    opacity: 0.5;
  }

  .expand-box {
    margin: 0.15rem 0.5rem 0.5rem 2.9rem;
    padding: 0.75rem 0.85rem;
    border-radius: 10px;
    background: var(--bg-tertiary);
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .expand-box input {
    width: 100%;
    font-size: max(16px, 1rem);
  }

  .expand-actions {
    display: flex;
    gap: 0.6rem;
    margin-top: 0.65rem;
  }

  .expand-yes,
  .expand-no {
    flex: 1;
    min-height: 40px;
    border-radius: 10px;
    font-size: 0.9375rem;
    border: none;
  }

  .expand-yes {
    background: var(--accent-color);
    color: #fff;
  }

  .expand-yes.danger {
    background: var(--error-text);
    color: var(--bg-primary);
  }

  .expand-no {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
  }

  .sheet-cancel {
    width: 100%;
    min-height: 48px;
    margin-top: 0.5rem;
    border: none;
    border-radius: 12px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    font-size: 1rem;
  }
</style>
